import React from 'react';
import type { Stats } from '@/components/wyscout/MatchAdvancedStats/types';
import { discoverTeamIds } from '@/components/wyscout/MatchAdvancedStats/discoverTeamIds';
import type { MatchEvent } from '@/components/wyscout/MatchEvents/types';
import { parseTeamNames } from '@/components/wyscout/WyscoutMatchDetail/helpers';
import { eventActionLabel, eventCategoryMatches, sortEventsChronologically } from './wyscout-events/helpers';

export interface MatchAnalysisIdentity {
  competition?: { name?: string };
  season?: { name?: string };
  kickoffAt: string;
  status: string;
  venueName?: string;
  label?: string;
  homeTeam: { id: number; name?: string };
  awayTeam: { id: number; name?: string };
  score: { home: number | null; away: number | null };
}

interface Props {
  match: MatchAnalysisIdentity;
  stats?: Stats;
  events?: MatchEvent[];
  showMetrics?: boolean;
}

const readableName = (value: string | undefined, fallback: string): string => {
  const name = value?.trim();
  return name && !/^\d+$/.test(name) ? name : fallback;
};

export const readableFact = (value: string | undefined): string | null => {
  const fact = value?.trim();
  return fact && !/^\d+$/.test(fact) ? fact : null;
};

export const getWyscoutTeamNames = (match: MatchAnalysisIdentity): { home: string; away: string } => {
  const parsed = parseTeamNames(match.label || '');

  return {
    home: readableName(match.homeTeam?.name, readableName(parsed.home, 'Home')),
    away: readableName(match.awayTeam?.name, readableName(parsed.away, 'Away')),
  };
};

const finiteNumber = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const nonNegativeNumber = (value: unknown): number | null => {
  const number = finiteNumber(value);
  return number !== null && number >= 0 ? number : null;
};

const percentage = (value: unknown): number | null => {
  const number = finiteNumber(value);
  return number !== null && number >= 0 && number <= 100 ? number : null;
};

const formatKickoff = (value: string): string | null => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatStatus = (value: string): string | null => {
  const status = value.trim().replace(/[_-]+/g, ' ');
  return status && !/^\d+$/.test(status) ? status.replace(/\b\w/g, character => character.toUpperCase()) : null;
};

export const WyscoutMatchMetrics: React.FC<{ match: MatchAnalysisIdentity; stats: Stats }> = ({ match, stats }) => {
  const teamNames = getWyscoutTeamNames(match);
  const ids = discoverTeamIds(stats, match.homeTeam.id, match.awayTeam.id, teamNames.home);

  const comparison = (label: string, home: number | null, away: number | null, format: (value: number) => string) => (
    home !== null && away !== null ? { label, home: format(home), away: format(away) } : null
  );
  const passAccuracy = (teamId: string): number | null => {
    const passes = finiteNumber(stats.passes?.[teamId]?.passes);
    const successful = finiteNumber(stats.passes?.[teamId]?.passesSuccessful);
    return passes !== null && passes > 0 && successful !== null && successful >= 0 && successful <= passes
      ? (successful / passes) * 100
      : null;
  };

  const metrics = [
    comparison('Expected goals', nonNegativeNumber(stats.general?.[ids.homeId]?.xg), nonNegativeNumber(stats.general?.[ids.awayId]?.xg), value => value.toFixed(2)),
    comparison('Shots', nonNegativeNumber(stats.general?.[ids.homeId]?.shots), nonNegativeNumber(stats.general?.[ids.awayId]?.shots), value => String(value)),
    comparison('Possession', percentage(stats.possession?.[ids.homeId]?.possessionPercent), percentage(stats.possession?.[ids.awayId]?.possessionPercent), value => `${value.toFixed(1).replace(/\.0$/, '')}%`),
    comparison('Pass accuracy', passAccuracy(ids.homeId), passAccuracy(ids.awayId), value => `${value.toFixed(1).replace(/\.0$/, '')}%`),
  ].filter((metric): metric is { label: string; home: string; away: string } => metric !== null);

  if (metrics.length === 0) return null;

  return (
    <dl className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {metrics.map(metric => (
        <div key={metric.label} className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/70">
          <dt className="text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{metric.label}</dt>
          <dd className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-center">
            <span className="text-xl font-bold tabular-nums text-club-blue-700 dark:text-blue-300">{metric.home}</span>
            <span className="text-xs text-gray-400">vs</span>
            <span className="text-xl font-bold tabular-nums text-red-700 dark:text-red-300">{metric.away}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
};

const WyscoutMatchOverview: React.FC<Props> = ({ match, stats, events, showMetrics = true }) => {
  const teamNames = getWyscoutTeamNames(match);
  const keyMoments = events
    ? sortEventsChronologically(events.filter(event => eventCategoryMatches(event, 'key'))).slice(0, 5)
    : [];

  const facts = [
    ['Competition', readableFact(match.competition?.name)],
    ['Season', readableFact(match.season?.name)],
    ['Kickoff', formatKickoff(match.kickoffAt)],
    ['Venue', readableFact(match.venueName)],
    ['Status', formatStatus(match.status)],
  ].filter((fact): fact is [string, string] => fact[1] !== null);

  return (
    <div className="min-w-0 space-y-6">
      {showMetrics && stats && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-6" aria-label="Match metrics">
          <WyscoutMatchMetrics match={match} stats={stats} />
        </section>
      )}

      {keyMoments.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-6" aria-labelledby="key-moments-heading">
          <h2 id="key-moments-heading" className="text-lg font-semibold text-gray-950 dark:text-white">Key moments</h2>
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-800">
            {keyMoments.map(event => {
              const teamName = event.team?.id === match.homeTeam.id ? teamNames.home : event.team?.id === match.awayTeam.id ? teamNames.away : 'Unknown team';
              return (
                <li key={event.id} className="flex min-w-0 items-baseline gap-3 py-2 text-sm">
                  <span className="shrink-0 font-semibold tabular-nums text-gray-950 dark:text-white">{event.minute}'</span>
                  <span className="min-w-0 text-gray-700 dark:text-gray-300"><strong>{eventActionLabel(event)}</strong>{event.player?.name ? `, ${event.player.name}` : ''} <span className="text-gray-500 dark:text-gray-400">({teamName})</span></span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {facts.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-6" aria-labelledby="match-facts-heading">
          <h2 id="match-facts-heading" className="text-lg font-semibold text-gray-950 dark:text-white">Match facts</h2>
          <dl className="mt-4 grid min-w-0 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {facts.map(([label, value]) => (
              <div key={label} className="min-w-0 border-b border-gray-100 pb-3 dark:border-gray-800">
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="mt-1 break-words text-sm font-medium text-gray-950 dark:text-white">{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
};

export default WyscoutMatchOverview;
